import { formatDateTime } from "@/lib/format";
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { glopicApi } from "@/lib/glopic-api";
import Pagination from "@/components/base/Pagination";
import FilterBar from "@/components/base/FilterBar";
import TableSortHeader from "@/components/base/TableSortHeader";
import UserIdentityCell from "@/components/base/UserIdentityCell";
import { buildGenerationQueryParams, generationStatusLabels } from "./contracts";

interface GenRow {
  gen_id: string;
  user_id: string;
  user_type: "consumer" | "merchant" | null;
  nickname: string | null;
  avatar: string | null;
  prompt: string | null;
  task_status: string;
  image_url: string | null;
  display_image_url: string | null;
  original_image_url: string | null;
  image_url_type: string | null;
  model_id: string | null;
  tool_id: string | null;
  mode: string | null;
  entry_id: string | null;
  source_entry: string | null;
  generation_operation: string | null;
  quality: string | null;
  cost_points: number;
  cached: boolean;
  failure_message: string | null;
  created_at: string;
}

const operationLabel = (operation: string | null | undefined) => {
  if (operation === "image_generate") return "生图(image_generate)";
  if (operation === "image_edit") return "图片编辑(image_edit)";
  return "--";
};

export default function AiGenerationsPage() {
  const navigate = useNavigate();
  const [gens, setGens] = useState<GenRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [userTypeFilter, setUserTypeFilter] = useState<string>("");
  const [sourceEntryFilter, setSourceEntryFilter] = useState<string>("");
  const [entryIdFilter, setEntryIdFilter] = useState<string>("");
  const [toolIdFilter, setToolIdFilter] = useState<string>("");
  const [timeRange, setTimeRange] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState("created_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const handleSort = useCallback((field: string, dir: "asc" | "desc") => {
    setSortField(field || "created_at");
    setSortDir(dir);
    setPage(1);
  }, []);

  const fetchGens = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildGenerationQueryParams({
        page,
        pageSize,
        userId: search,
        status: statusFilter,
        userType: userTypeFilter,
        sourceEntry: sourceEntryFilter,
        entryId: entryIdFilter,
        toolId: toolIdFilter,
        timeRange,
        startDate,
        endDate,
      });

      const response = await glopicApi.get("/generations", { params });
      const data = response.data.data;
      setGens(data.items ?? []);
      setTotal(data.total ?? 0);
    } catch (err) {
      console.error("Fetch generations error:", err);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter, userTypeFilter, sourceEntryFilter, entryIdFilter, toolIdFilter, timeRange, startDate, endDate, sortField, sortDir, page, pageSize]);

  useEffect(() => {
    fetchGens();
  }, [fetchGens]);

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-text-primary">生成记录</h2>
        <span className="text-text-muted text-sm">共 {total} 次生成</span>
      </div>

      {/* Filters */}
      <FilterBar
        search={search}
        onSearchChange={(val) => { setSearch(val); setPage(1); }}
        searchPlaceholder="搜索用户ID..."
        timeRange={timeRange}
        onTimeRangeChange={(val) => { setTimeRange(val); setPage(1); }}
        customDateRange={{ start: startDate, end: endDate }}
        onCustomDateChange={(start, end) => { setStartDate(start); setEndDate(end); }}
        filters={[
          {
            key: "status",
            label: "全部状态",
            type: "select",
            options: [
              { value: "created", label: "已创建" },
              { value: "queued", label: "排队中" },
              { value: "generating", label: "生成中" },
              { value: "auditing", label: "审核中" },
              { value: "success", label: "成功" },
              { value: "failed", label: "失败" },
              { value: "blocked", label: "已拦截" },
              { value: "timeout", label: "已超时" },
              { value: "cancelled", label: "已取消" },
              { value: "refunded", label: "已退款" },
              { value: "pending", label: "等待中" },
              { value: "processing", label: "处理中" },
            ],
          },
          {
            key: "user_type",
            label: "全部用户类型",
            type: "select",
            options: [
              { value: "consumer", label: "C端用户" },
              { value: "merchant", label: "商家用户" },
            ],
          },
        ]}
        filterValues={{ status: statusFilter, user_type: userTypeFilter }}
        onFilterChange={(key, val) => {
          if (key === "status") setStatusFilter(val);
          if (key === "user_type") setUserTypeFilter(val);
          setPage(1);
        }}
        activeCount={[
          search,
          statusFilter,
          userTypeFilter,
          sourceEntryFilter,
          entryIdFilter,
          toolIdFilter,
          timeRange,
        ].filter(Boolean).length}
        onReset={() => {
          setSearch("");
          setStatusFilter("");
          setUserTypeFilter("");
          setSourceEntryFilter("");
          setEntryIdFilter("");
          setToolIdFilter("");
          setTimeRange("");
          setStartDate("");
          setEndDate("");
          setSortField("created_at");
          setSortDir("desc");
          setPage(1);
        }}
      >
        <input
          value={sourceEntryFilter}
          onChange={(event) => { setSourceEntryFilter(event.target.value); setPage(1); }}
          placeholder="来源 source_entry"
          className="h-10 min-w-[160px] px-3 rounded-lg bg-surface text-sm text-text-primary border border-surface-border focus:border-brand/50 focus:outline-none"
        />
        <input
          value={entryIdFilter}
          onChange={(event) => { setEntryIdFilter(event.target.value); setPage(1); }}
          placeholder="入口 entry_id"
          className="h-10 min-w-[150px] px-3 rounded-lg bg-surface text-sm text-text-primary border border-surface-border focus:border-brand/50 focus:outline-none"
        />
        <input
          value={toolIdFilter}
          onChange={(event) => { setToolIdFilter(event.target.value); setPage(1); }}
          placeholder="工具 tool_id"
          className="h-10 min-w-[140px] px-3 rounded-lg bg-surface text-sm text-text-primary border border-surface-border focus:border-brand/50 focus:outline-none"
        />
      </FilterBar>

      {/* Stats bar */}
      <div className="flex items-center gap-4 text-xs text-text-muted px-1">
        <span>成功: {gens.filter((g) => g.task_status === "success").length}</span>
        <span>失败: {gens.filter((g) => g.task_status === "failed").length}</span>
        <span>总消耗: {gens.reduce((s, g) => s + g.cost_points, 0)} pts</span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-surface-border overflow-hidden">
        <div className="overflow-x-auto -mx-0">
          <table className="w-full text-sm min-w-[1080px]">
            <thead>
              <tr className="border-b border-surface-border bg-surface/50">
                <th className="text-left px-3 md:px-5 py-3 md:py-3.5 text-text-secondary font-medium text-xs">用户</th>
                <th className="text-left px-3 md:px-5 py-3 md:py-3.5 text-text-secondary font-medium text-xs">提示词</th>
                <th className="text-left px-3 md:px-5 py-3 md:py-3.5 text-text-secondary font-medium text-xs">状态</th>
                <th className="text-left px-3 md:px-5 py-3 md:py-3.5 text-text-secondary font-medium text-xs">模型</th>
                <th className="text-left px-3 md:px-5 py-3 md:py-3.5 text-text-secondary font-medium text-xs">入口/工具</th>
                <th className="text-left px-3 md:px-5 py-3 md:py-3.5 text-text-secondary font-medium text-xs">执行类型</th>
                <th className="text-left px-3 md:px-5 py-3 md:py-3.5 text-text-secondary font-medium text-xs">画质</th>
                <TableSortHeader label="消耗" field="cost_points" currentField={sortField} currentDir={sortDir} onSort={handleSort} />
                <TableSortHeader label="时间" field="created_at" currentField={sortField} currentDir={sortDir} onSort={handleSort} className="hidden sm:table-cell" />
                <th className="text-right px-3 md:px-5 py-3 md:py-3.5 text-text-secondary font-medium text-xs">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} className="px-5 py-12 text-center text-text-muted">
                    <i className="ri-loader-4-line animate-spin text-2xl mb-2 block" />
                    加载中...
                  </td>
                </tr>
              ) : gens.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-5 py-12 text-center text-text-muted">
                    <i className="ri-image-ai-line text-3xl mb-2 block" />
                    暂无生成记录
                  </td>
                </tr>
              ) : (
                gens.map((g) => {
                  const statusCfg = generationStatusLabels[g.task_status] || {
                    cls: "bg-text-muted/10 text-text-muted",
                    label: g.task_status || "未知状态",
                  };
                  return (
                    <tr key={g.gen_id} className="border-b border-surface-border hover:bg-surface/30 transition-colors">
                      <td className="px-3 md:px-5 py-3 md:py-4">
                        <UserIdentityCell nickname={g.nickname} userId={g.user_id} avatar={g.avatar} />
                        <span className={`mt-1 inline-flex px-1.5 py-0.5 rounded text-[10px] ${g.user_type === "merchant" ? "bg-violet-500/10 text-violet-600" : "bg-blue-500/10 text-blue-600"}`}>
                          {g.user_type === "merchant" ? "商家用户" : g.user_type === "consumer" ? "C端用户" : "未知类型"}
                        </span>
                      </td>
                      <td className="px-3 md:px-5 py-3 md:py-4">
                        <p className="text-text-secondary text-xs md:text-sm truncate max-w-[160px]" title={g.prompt || "--"}>
                          {g.prompt || "--"}
                        </p>
                      </td>
                      <td className="px-3 md:px-5 py-3 md:py-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium ${statusCfg.cls}`}>
                          {g.cached && g.task_status === "success" && <i className="ri-refresh-line" />}
                          {statusCfg.label}
                        </span>
                      </td>
                      <td className="px-3 md:px-5 py-3 md:py-4 text-text-muted text-xs">{g.model_id || "--"}</td>
                      <td className="px-3 md:px-5 py-3 md:py-4 text-text-muted text-xs">
                        <div>来源: {g.source_entry || "--"}</div>
                        <div>入口: {g.entry_id || "--"}</div>
                        <div>工具: {g.tool_id || "--"}</div>
                      </td>
                      <td className="px-3 md:px-5 py-3 md:py-4 text-text-muted text-xs">{operationLabel(g.generation_operation)}</td>
                      <td className="px-3 md:px-5 py-3 md:py-4 text-text-muted text-xs">{g.quality || "--"}</td>
                      <td className="px-3 md:px-5 py-3 md:py-4 font-mono text-text-primary text-xs">{g.cost_points} pts</td>
                      <td className="px-3 md:px-5 py-3 md:py-4 text-text-muted text-xs hidden sm:table-cell">
                        {formatDateTime(g.created_at)}
                      </td>
                      <td className="px-3 md:px-5 py-3 md:py-4 text-right">
                        <button
                          onClick={() => navigate(`/ai/generations/${g.gen_id}`)}
                          className="text-brand text-xs hover:underline cursor-pointer"
                        >
                          详情
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          />
        )}
      </div>
    </div>
  );
}
